import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Realities() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesignation, setNewDesignation] = useState("");

  const load = () => api.get("/realities").then((r) => setItems(r.data));

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter((it) =>
      [it.title, it.designation, it.body, it.id]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [items, query]);

  const create = async () => {
    if (!newTitle.trim()) return;
    try {
      await api.post("/realities", {
        title: newTitle.trim(),
        designation: newDesignation.trim(),
        classification: "UNCLASSIFIED",
        status: "ACTIVE",
        date: "",
        body: "",
        images: [],
      });
      setNewTitle("");
      setNewDesignation("");
      setCreating(false);
      toast.success("Reality inscribed.");
      load();
    } catch (e) {
      toast.error("Inscription failed.");
    }
  };

  return (
    <div className="space-y-12" data-testid="realities-page">
      <header className="space-y-6">
        <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
          Section IV · Catalogus Realitatum
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tighter leading-none">
          Realities
        </h1>
        <p className="text-sm text-[#A3A3A3] max-w-2xl leading-relaxed">
          Adjacent worlds catalogued by drift, by reflection, by accidental crossing.
          Each entry below is a confirmed thread in the manifold.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center pt-4 border-t border-[#333]">
          <input
            className="tiv-input sm:max-w-sm"
            placeholder="search title, designation, contents..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="realities-search-input"
          />
          <div className="text-[0.65rem] tracking-[0.3em] uppercase text-[#555] sm:ml-auto">
            {filtered.length} catalogued
          </div>
          {isAdmin && !creating && (
            <button className="tiv-btn" onClick={() => setCreating(true)} data-testid="realities-new-btn">
              + Inscribe Reality
            </button>
          )}
        </div>

        {isAdmin && creating && (
          <div className="border border-[#333] p-5 bg-[#0a0a0a] grid grid-cols-1 sm:grid-cols-12 gap-3 sm:items-end">
            <label className="sm:col-span-3">
              <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-1">Designation</div>
              <input
                className="tiv-input"
                placeholder="R-γ"
                value={newDesignation}
                onChange={(e) => setNewDesignation(e.target.value)}
                data-testid="realities-new-designation"
              />
            </label>
            <label className="sm:col-span-6">
              <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-1">Title</div>
              <input
                autoFocus
                className="tiv-input"
                placeholder="Title of this reality..."
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && create()}
                data-testid="realities-new-title"
              />
            </label>
            <div className="sm:col-span-3 flex gap-2">
              <button className="tiv-btn flex-1" onClick={create} data-testid="realities-new-save">Inscribe</button>
              <button className="tiv-btn" onClick={() => { setCreating(false); setNewTitle(""); setNewDesignation(""); }} data-testid="realities-new-cancel">Discard</button>
            </div>
          </div>
        )}
      </header>

      {filtered.length === 0 ? (
        <div className="text-[#555] text-xs tracking-widest uppercase border border-dashed border-[#333] p-16 text-center">
          ::: no realities matched :::
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-[#333]">
          {filtered.map((it) => (
            <Link
              key={it.id}
              to={`/realities/${it.id}`}
              className="border-r border-b border-[#333] p-6 hover:bg-[#E3E1D9] hover:text-[#050505] group flex flex-col gap-3 min-h-[200px]"
              data-testid={`reality-card-${it.id}`}
            >
              <div className="flex justify-between items-start">
                <div className="text-xs tracking-[0.3em] text-[#A3A3A3] group-hover:text-[#050505]">
                  {it.designation || it.id}
                </div>
                <span className={`status-pill ${
                  it.status === "ACTIVE" ? "status-active" :
                  it.status === "REDACTED" ? "status-redacted" : "status-lost"
                }`}>{it.status}</span>
              </div>
              <div className="font-serif-display text-3xl leading-tight">
                {it.title || <span className="italic text-[#555]">[ untitled ]</span>}
              </div>
              <div className="text-sm text-[#A3A3A3] group-hover:text-[#050505]/80 line-clamp-3 leading-relaxed">
                {it.body || <span className="italic">no observation recorded</span>}
              </div>
              <div className="mt-auto text-[0.6rem] tracking-[0.35em] uppercase text-[#555] group-hover:text-[#050505]/70">
                {it.classification} {it.date && `· ${it.date}`}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
